import { action, makeObservable, observable } from 'mobx';
import { get, post } from 'stores/_services/dataService';
import { ILocation } from 'stores/location/LocationStore';

export interface IBmsIntegration {
    userName: string;
    password?: string;
    serverName?: string;
    companyName?: string;
    mappings?: Record<string, unknown>;
    queue?: string;
    ticketType?: string;
    severity?: Array<string>;
    sourceType?: Array<string>;
}

export default class BmsIntegrationStore {
    integration = {
        userName: ''
    };

    constructor() {
        makeObservable(this, {
            integration: observable,
            update: action,
            getBmsConfig: action,
            checkConnection: action,
            checkConfig: action,
            setIntegration: action,
            changeEnabled: action,
            setSettings: action,
            getMappings: action,
            setMappings: action,
            getOptions: action
        });
    }

    update(integration: IBmsIntegration): void {
        this.integration = {
            ...this.integration,
            ...integration
        };
    }

    async getBmsConfig(): Promise<void> {
        const { data } = await get('bmsconfiguration/config');
        return data;
    }

    async checkConnection(): Promise<void> {
        const { data } = await get('bmsconfiguration/checkConnection');
        return data;
    }

    async checkConfig(): Promise<void> {
        const { data } = await get('bmsconfiguration/checkConfig');
        return data;
    }

    async setIntegration(data: Record<string, unknown>): Promise<void> {
        return await post('bmsconfiguration/config', {
            username: data.userName,
            password: data.password,
            servername: data.serverName,
            companyname: data.companyName,
            enabled: true
        });
    }

    async changeEnabled(bool: boolean) {
        return await post('bmsconfiguration/config', {
            enabled: bool
        });
    }

    async setSettings(data: any, mappings: any): Promise<Record<string, unknown>> {
        const configRes = await post('bmsconfiguration/config', {
            severities: Object.entries(data.severity)
                ?.filter(([key, val]) => val === true)
                .map(([key, val]) => key),
            sourceTypes: Object.entries(data.sourceType)
                ?.filter(([key, val]) => val === true)
                .map(([key, val]) => key),
            issueTypeId: data.issueType,
            queueId: data.queue,
            contract: data.contract
        });
        const mapRes = await post('bmsconfiguration/mappings/severities', mappings);

        return { configRes, mapRes };
    }

    async getMappings(): Promise<Record<string, unknown>> {
        const { data: locations } = await get('Locations', {
            filter: {
                order: 'name ASC',
                include: 'organization'
            }
        });
        const {
            data: { mappings, originalAccounts }
        } = await get('bmsconfiguration/mappings/accounts');
        const { data: accountTypes } = await get('bmsconfiguration/accountTypes');

        locations.forEach((loc: ILocation) => {
            if (loc.organization) {
                loc.name = `${loc.name} (${loc.organization.name})`;
            }
        });

        return { locations, mappings, accountTypes, originalAccounts };
    }

    async getBmsAccounts(accountType: string): Promise<Record<string, unknown>> {
        const { data: accounts } = await get(`bmsconfiguration/summaryByAccountTypeName?accountTypeName=${accountType}`);
        return { accounts };
    }

    async setMappings(data: Record<string, unknown>): Promise<void> {
        return await post('bmsconfiguration/mappings/accounts', data);
    }

    async unmapAccount(mappingId: string): Promise<void> {
        await post('bmsconfiguration/mappings/accounts/unmap', { mappingId });
    }

    async getOptions(): Promise<Record<string, unknown>> {
        const { data: config } = await get('bmsconfiguration/config');
        const { data: mappings } = await get('bmsconfiguration/mappings/severities');
        const { data: queues } = await get('bmsconfiguration/queueNames');
        const { data: issueTypes } = await get('bmsconfiguration/issueTypes');
        const { data: priorities } = await get('bmsconfiguration/priorities');

        return { config, mappings, queues, issueTypes, priorities };
    }
}
